import React, { useEffect, useRef, useState } from "react";
import { X, Loader2 } from "lucide-react";

const CDN_CSS = "https://cdn.jsdelivr.net/npm/pannellum@2.5.6/build/pannellum.css";
const CDN_JS = "https://cdn.jsdelivr.net/npm/pannellum@2.5.6/build/pannellum.js";

let loaderPromise = null;

export function loadPannellum() {
  if (typeof window !== "undefined" && window.pannellum) return Promise.resolve(window.pannellum);
  if (loaderPromise) return loaderPromise;
  loaderPromise = new Promise((resolve, reject) => {
    // CSS
    if (!document.querySelector(`link[href="${CDN_CSS}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = CDN_CSS;
      document.head.appendChild(link);
    }
    // JS
    if (window.pannellum) return resolve(window.pannellum);
    const script = document.createElement("script");
    script.src = CDN_JS;
    script.async = true;
    script.onload = () => resolve(window.pannellum);
    script.onerror = () => reject(new Error("Failed to load Pannellum"));
    document.body.appendChild(script);
  });
  return loaderPromise;
}

/**
 * PanoramaViewer — full-screen modal wrapping an interactive Pannellum viewer.
 * Props: imageUrl (equirectangular JPG/PNG), onClose(), title
 */
const PanoramaViewer = ({ imageUrl, onClose, title }) => {
  const containerRef = useRef(null);
  const viewerRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const pannellum = await loadPannellum();
        if (cancelled || !containerRef.current) return;
        viewerRef.current = pannellum.viewer(containerRef.current, {
          type: "equirectangular",
          panorama: imageUrl,
          autoLoad: true,
          autoRotate: -2,
          compass: false,
          mouseZoom: true,
          hfov: 100,
          showControls: true,
          showZoomCtrl: true,
          showFullscreenCtrl: true,
        });
        setReady(true);
      } catch (e) {
        setError("Could not load the 360° viewer.");
      }
    })();

    // Close on Escape
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);

    return () => {
      cancelled = true;
      window.removeEventListener("keydown", onKey);
      try { viewerRef.current?.destroy?.(); } catch { /* noop */ }
    };
  }, [imageUrl, onClose]);

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 flex flex-col"
      role="dialog"
      aria-modal="true"
      data-testid="panorama-viewer"
    >
      <div className="flex items-center justify-between px-4 py-3 text-[#F5EFE6]">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#B76E79]">360° tour</span>
          {title && <span className="text-sm">· {title}</span>}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-2 rounded-full hover:bg-white/10"
          aria-label="Close 360° viewer"
          data-testid="panorama-close"
        >
          <X size={20} />
        </button>
      </div>
      <div className="flex-1 relative">
        <div ref={containerRef} className="absolute inset-0" />
        {!ready && !error && (
          <div className="absolute inset-0 flex items-center justify-center text-[#F5EFE6]">
            <Loader2 className="animate-spin" size={28} />
          </div>
        )}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center text-[#F5EFE6]">
            {error}
          </div>
        )}
      </div>
      <div className="px-4 py-2 text-center text-[10px] uppercase tracking-[0.18em] text-[#F5EFE6]/70">
        Drag to look around · scroll to zoom · Esc to close
      </div>
    </div>
  );
};

export default PanoramaViewer;
